import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { entityDetailUrlMatcher } from 'src/lib/routing/entity-detail-route-matcher';
import { AuthenticationGuard } from './authentication/guards/authentication.guard';
import { LoginGuard } from './authentication/guards/login.guard';
import { SystemUserGuard } from './authentication/guards/system-user.guard';
import { ComponentCanDeactivateGuard } from './form/component-deactivation.guard';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { AccountDetailComponent } from './pages/account-details/containers/account-detail.component';
import { AccountRegisterComponent } from './pages/account-register/containers/account-register.component';
import { AccountVerificationComponent } from './pages/account-verification/containers/account-verification.component';
import { AccountActiveCallsRegisterComponent } from './pages/active-calls/account-active-calls-register/containers/account-active-calls-register.component';
import { AccountCallHistoryRegisterComponent } from './pages/active-calls/account-call-history-register/containers/account-call-history-register.component';
import { SystemActiveCallsRegisterComponent } from './pages/active-calls/system-active-calls-register/containers/system-active-calls-register.component';
import { AllowedIpRegisterComponent } from './pages/allowed-ips-register/containers/allowed-ip-register.component';
import { BannedProvisioningIpRegisterComponent } from './pages/banned-provisioning-ip-register/containers/banned-provisioning-ip-register.component';
import { BlockedPhoneNumberRegisterComponent } from './pages/blocked-phone-number-register-component/containers/blocked-phone-number-register.component';
import { CallCostRegisterComponent } from './pages/call-cost-register/containers/call-cost-register.component';
import { DashboardComponent } from './pages/dashboard/containers/dashboard.component';
import { DepartmentWrapperComponent } from './pages/department-register/container/department-wrapper.component';
import { DeviceDetailComponent } from './pages/device-details/containers/device-detail.component';
import { AccountDeviceRegisterComponent } from './pages/device-register/account-device-register/containers/account-device-register.component';
import { SystemDeviceRegisterComponent } from './pages/device-register/system-device-register/containers/system-device-register.component';
import { NotFoundComponent } from './pages/error-page/not-found/not-found.component';
import { ExtensionDetailComponent } from './pages/extension-details/containers/extension-detail.component';
import { ExtensionGroupDetailComponent } from './pages/extension-group-details/containers/extension-group-detail.component';
import { ExtensionGroupRegisterComponent } from './pages/extension-groups-register/containers/extension-group-register.component';
import { ExtensionRegisterComponent } from './pages/extension-register/containers/extension-register.component';
import { LocationDetailComponent } from './pages/location-details/containers/location-detail.component';
import { LocationsRegisterComponent } from './pages/locations-register-component/containers/locations-register.component';
import { LoginComponent } from './pages/login/views/login.component';
import { MailboxDetailStandaloneComponent } from './pages/mailbox-details/containers/mailbox-standalone-detail.component';
import { MailboxRegisterComponent } from './pages/mailbox-register/containers/mailbox-register.component';
import { PhoneNumberDetailComponent } from './pages/phone-number-details/containers/phone-number-detail.component';
import { AccountPhoneNumberRegisterComponent } from './pages/phone-number-register/account-phone-number-register/containers/account-phone-number-register.component';
import { SystemPhoneNumberRegisterComponent } from './pages/phone-number-register/system-phone-number-register/containers/system-phone-number-register.component';
import { PresenceContactRegisterComponent } from './pages/presence-contact-register/containers/presence-contact-register.component';
import { PromptDetailComponent } from './pages/prompt-details/containers/prompt-detail.component';
import { PromptRegisterComponent } from './pages/prompt-register/containers/prompt-register.component';
import { QueueHistoryRegisterComponent } from './pages/queue-history-register/containers/queue-history-register.component';
import { RoleDetailComponent } from './pages/role-details/containers/role-detail.component';
import { AccountRoleRegisterComponent } from './pages/role-register/account-role-register/containers/account-role-register.component';
import { SystemRoleRegisterComponent } from './pages/role-register/system-role-register/containers/system-role-register.component';
import { ScheduleWrapperComponent } from './pages/schedule-register/containers/schedule-wrapper.component';
import { SiteLayoutComponent } from './pages/site-layout/containers/site-layout.component';
import { AccountLogRegisterComponent } from './pages/system-log-register/account-log-register/containers/account-log-register.component';
import { SystemLogRegisterComponent } from './pages/system-log-register/system-log-register/containers/system-log-register.component';
import { UserDetailComponent } from './pages/user-details/containers/user-detail.component';
import { UserPasswordResetComponent } from './pages/user-password-reset/container/user-password-reset.component';
import { UserPreferenceDetailComponent } from './pages/user-preferences/containers/user-preference-detail.component';
import { AccountUserRegisterComponent } from './pages/user-register/containers/account-user-register/account-user-register.component';
import { SystemUserRegisterComponent } from './pages/user-register/containers/system-user-register/system-user-register.component';
import { PrivilegeGuard } from './privileges/privilege-guard';
import { Privileges } from './privileges/privileges';

export const defaultTitle = 'ndt';
export const dashboardRoute = 'dashboard';
export const systemUsersRoute = 'systemusers';
export const usersRoute = 'users';
export const accountsRoute = 'accounts';
export const accountLogsRoute = 'accountlogs';
export const accountSettingsRoute = 'accountsettings';
export const activeCallsRoute = 'activecalls';
export const accountActiveCallsRoute = 'accountactivecalls';
export const accountCallHistoryRoute = 'accountcallhistory';
export const accountQueuesRoute = 'accountqueues';
export const extensionsRoute = 'extensions';
export const presenceContactsRoute = 'presencecontacts';
export const mailboxesRoute = 'mailboxes';
export const phoneNumbersRoute = 'phonenumbers';
export const accountPhoneNumbersRoute = 'accountphonenumbers';
export const accountDevicesRoute = 'accountdevices';
export const phonesRoute = 'phones';
export const promptsRoute = 'prompts';
export const devicesRoute = 'devices';
export const blockedPhoneNumbersRoute = 'blockedPhoneNumbers';
export const allowedIpsRoute = 'allowedIps';
export const locationsRoute = 'locations';
export const extensionGroupRoute = 'extensiongroups';
export const schedulesRoute = 'schedules';
export const systemRolesRoute = 'systemroles';
export const systemLogsRoute = 'systemlogs';
export const rolesRoute = 'roles';
export const callCostsRoute = 'callcosts';
export const bannedIpsRoute = 'bannedIps';
export const departmentsRoute = 'departments';
export const userPreferencesRoute = 'userpreferences';

const routes: Routes = [
   {
      canActivate: [LoginGuard],
      component: LoginComponent,
      path: 'login',
   },
   {
      canActivate: [LoginGuard],
      component: LoginComponent,
      data: { entityName: defaultTitle },
      path: 'login-info',
   },
   {
      canActivate: [LoginGuard],
      component: LoginComponent,
      data: { entityName: defaultTitle },
      path: 'sso-login-timeout',
   },
   {
      canActivate: [LoginGuard],
      component: LoginComponent,
      data: { entityName: defaultTitle },
      path: 'sso-login-failure',
   },
   {
      component: LoginComponent,
      path: 'logout',
   },
   {
      component: AccountVerificationComponent,
      data: {
         entityName: 'AccountVerification',
      },
      path: 'account-verification',
   },
   {
      component: UserPasswordResetComponent,
      data: { entityName: defaultTitle },
      path: 'password-reset',
   },
   {
      canActivate: [AuthenticationGuard],
      canActivateChild: [AuthenticationGuard],
      children: [
         {
            path: '',
            pathMatch: 'full',
            redirectTo: 'dashboard',
         },
         {
            component: DashboardComponent,
            path: 'dashboard',
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountUserRegisterComponent,
            data: { privilege: Privileges.ManageUsersAndPrivileges_View },
            path: usersRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: UserDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageUsersAndPrivileges_View },
            matcher: entityDetailUrlMatcher(usersRoute),
            canDeactivate: [ComponentCanDeactivateGuard],
         },
         {
            canActivate: [PrivilegeGuard, SystemUserGuard],
            component: SystemUserRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageUsersAndPrivileges_View },
            path: systemUsersRoute,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: UserDetailComponent,
            data: { isDetailPage: true, isSystemUserPage: true, privilege: Privileges.ManageUsersAndPrivileges_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(systemUsersRoute),
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: AccountRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageAccountSettings_View },
            path: accountsRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageAccountSettings_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(accountSettingsRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountActiveCallsRegisterComponent,
            data: { privilege: Privileges.ManageActiveCalls_View },
            path: `${accountActiveCallsRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountCallHistoryRegisterComponent,
            data: { privilege: Privileges.ManageCallDetails_View },
            path: `${accountCallHistoryRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: QueueHistoryRegisterComponent,
            data: { privilege: Privileges.ManageQueueStatus_View },
            path: `${accountQueuesRoute}/:tab`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: QueueHistoryRegisterComponent,
            data: { privilege: Privileges.ManageQueueStatus_View },
            path: `${accountQueuesRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: SystemActiveCallsRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageActiveCalls_View },
            path: `${activeCallsRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: ExtensionRegisterComponent,
            data: { privilege: Privileges.ManageExtensions_Enabled },
            path: extensionsRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: ExtensionDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageExtensions_Enabled },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(extensionsRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: PresenceContactRegisterComponent,
            data: { privilege: Privileges.ManageExtensions_Enabled },
            path: presenceContactsRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: MailboxRegisterComponent,
            data: { privilege: Privileges.ManageExtensions_Enabled },
            path: mailboxesRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: MailboxDetailStandaloneComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageExtensions_Enabled },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(mailboxesRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: SystemLogRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageSystemLogs_View },
            path: systemLogsRoute,
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountPhoneNumberRegisterComponent,
            data: { privilege: Privileges.ManagePhoneNumberSettings_View },
            path: `${accountPhoneNumbersRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: PhoneNumberDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManagePhoneNumberSettings_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(accountPhoneNumbersRoute),
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: SystemPhoneNumberRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManagePhoneNumberSettings_View },
            path: `${phoneNumbersRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: PhoneNumberDetailComponent,
            data: { isDetailPage: true, isSystemUserPage: true, privilege: Privileges.ManagePhoneNumberSettings_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(phoneNumbersRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: BlockedPhoneNumberRegisterComponent,
            data: { privilege: Privileges.ManageAccountSettings_View },
            path: `${blockedPhoneNumbersRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: SystemRoleRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageRoles_View },
            path: `${systemRolesRoute}`,
         },
         {
            canActivate: [SystemUserGuard],
            component: AllowedIpRegisterComponent,
            data: { isSystemUserPage: true },
            path: `${allowedIpsRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: AccountRoleRegisterComponent,
            data: { privilege: Privileges.ManageRoles_View },
            path: `${rolesRoute}`,
         },
         {
            path: `${systemRolesRoute}/:id`,
            redirectTo: `${systemRolesRoute}/:id/role`,
            pathMatch: 'full',
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: RoleDetailComponent,
            data: { isDetailPage: true, isSystemUserPage: true, privilege: Privileges.ManageRoles_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(systemRolesRoute),
         },
         {
            path: `${rolesRoute}/:id`,
            redirectTo: `${rolesRoute}/:id/role`,
            pathMatch: 'full',
         },
         {
            canActivate: [PrivilegeGuard],
            component: RoleDetailComponent,
            data: { isDetailPage: true, isSystemUserPage: false, privilege: Privileges.ManageRoles_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(rolesRoute),
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: DeviceDetailComponent,
            data: { isDetailPage: true, isSystemUserPage: true, privilege: Privileges.ManageDevices_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(devicesRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: LocationsRegisterComponent,
            data: { privilege: Privileges.ManageLocationSettings_View },
            path: `${locationsRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: LocationDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageLocationSettings_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(locationsRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: ExtensionGroupRegisterComponent,
            data: { privilege: Privileges.ManageExtensions_Enabled },
            path: `${extensionGroupRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: ExtensionGroupDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageExtensions_Enabled },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(extensionGroupRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: ScheduleWrapperComponent,
            data: { privilege: Privileges.ManageSchedules_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(schedulesRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: ScheduleWrapperComponent,
            data: { privilege: Privileges.ManageSchedules_View },
            path: `${schedulesRoute}`,
            canDeactivate: [ComponentCanDeactivateGuard],
         },
         {
            canActivate: [PrivilegeGuard],
            component: DepartmentWrapperComponent,
            data: { privilege: Privileges.ManageDepartments_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(departmentsRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: DepartmentWrapperComponent,
            data: { privilege: Privileges.ManageDepartments_View },
            path: `${departmentsRoute}`,
            canDeactivate: [ComponentCanDeactivateGuard],
         },
         {
            canActivate: [PrivilegeGuard],
            component: DeviceDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageDevices_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(accountDevicesRoute),
         },
         {
            canActivate: [PrivilegeGuard],
            component: PromptRegisterComponent,
            data: { privilege: Privileges.ManageAccountPrompts_View },
            path: `${promptsRoute}`,
         },
         {
            canActivate: [PrivilegeGuard],
            component: PromptDetailComponent,
            data: { isDetailPage: true, privilege: Privileges.ManageAccountPrompts_View },
            canDeactivate: [ComponentCanDeactivateGuard],
            matcher: entityDetailUrlMatcher(promptsRoute),
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: AccountDeviceRegisterComponent,
            data: { privilege: Privileges.ManageDevices_View },
            path: `${accountDevicesRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: AccountLogRegisterComponent,
            data: { privilege: Privileges.ManageSystemLogs_View },
            path: `${accountLogsRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: SystemDeviceRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageDevices_View },
            path: `${devicesRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: BannedProvisioningIpRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageBannedAllowedIps_Enabled },
            path: `${bannedIpsRoute}`,
         },
         {
            canActivate: [SystemUserGuard, PrivilegeGuard],
            component: CallCostRegisterComponent,
            data: { isSystemUserPage: true, privilege: Privileges.ManageSystemLogs_View },
            path: `${callCostsRoute}`,
         },
         {
            component: UserPreferenceDetailComponent,
            path: `${userPreferencesRoute}`,
         },
         {
            data: { entityName: 'Report List' },
            loadChildren: () => {
               return import('@ndt/reports/reports.module').then((m) => {
                  return m.ReportsModule;
               });
            },
            path: 'reports',
         },
         {
            component: AccessDeniedComponent,
            path: 'access-denied',
         },
         { component: NotFoundComponent, data: { entityName: `Page Not Found - ${defaultTitle}` }, path: '**' },
      ],
      component: SiteLayoutComponent,
      path: '',
   },
];

@NgModule({
   exports: [RouterModule],
   imports: [
      RouterModule.forRoot(routes, {
         paramsInheritanceStrategy: 'always',
         preloadingStrategy: PreloadAllModules,
         scrollPositionRestoration: 'enabled',
         // enableTracing: true,
      }),
   ],
})
export class AppRoutingModule {}
